import { Aide } from '@/components/ui/aide';
import MessagesClientSwitch from '../components/clients/MessagesClientSwitch';
import FinancialStatement from '../components/FinancialStatement';
import React, { useEffect, useMemo, useState } from 'react';
import { clientsAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  cn,
  formatCurrency,
  formatCompactCurrency,
} from '../lib/utils';
import { PageHeader } from '../components/ui/page-header';
import { LoadingState } from '../components/ui/loading-state';
import { EmptyState } from '../components/ui/empty-state';
import { ChampsAvances } from '../components/ui/champs-avances';
import { SectionCard } from '../components/dashboard';
import { LEXIQUE } from '../lib/lexique';
import { libelle, referenceLisible, texteSur } from '../lib/libelles';
import { formatDate } from '../lib/formats';
import { messageErreur } from '../lib/erreurs';
import { toast } from '../components/ui/sonner';
import { BookUser, HelpCircle, Plus, Search } from 'lucide-react';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from '../components/ui/pagination-bar';

const createDefaultClientForm = () => ({
  name: '',
  // `segment` est nullable côté serveur, sans valeur par défaut: ne rien
  // envoyer laisserait le client hors du filtre « Tiers », donc invisible dans
  // sa propre liste. Le formulaire de création ne pose plus la question, mais
  // la valeur de départ continue de partir.
  category: 'TIER',
  phone: '',
  station_name: '',
  quarter: '',
  city: '',
  credit_limit: '',
  default_price_per_liter: '',
  notes: '',
});

const getClientCategoryLabel = (client) => {
  const value = client?.segment || client?.type || 'TIER';
  if (value === 'OCCASIONAL') return 'Occasionnel';
  if (value === 'LOYAL') return 'Fidèle';
  return 'Tiers';
};

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('ALL');
  const [isLoading, setIsLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState(createDefaultClientForm);

  const loadClients = async ({ silencieux = false } = {}) => {
    if (!silencieux) setIsLoading(true);
    try {
      const result = await fetchAllPages((params) => clientsAPI.getAllPaged(params));
      setClients(result.items || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
      toast.error(messageErreur(err, 'Impossible de charger les clients'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['clients', 'factures', 'paiements', 'commandes', 'finance'], loadClients);


  const filtered = clients.filter((c) => {
    const matchesSearch = String(c.name || '')
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesSegment = segmentFilter === 'ALL' || (c.segment || c.type || 'TIER') === segmentFilter;
    return matchesSearch && matchesSegment;
  });
  const pagination = usePagination(filtered, { reinitialiser: `${searchQuery}|${segmentFilter}` });

  const pilotage = useMemo(() => {
    const activeClients = clients.filter((client) => (client.status || 'ACTIVE') === 'ACTIVE');
    const totalBalance = clients.reduce((sum, client) => sum + Number(client.current_balance || 0), 0);
    const totalCreditLimit = clients.reduce((sum, client) => sum + Number(client.credit_limit || 0), 0);
    const riskyClients = clients.filter(
      (client) =>
        Number(client.credit_limit || 0) > 0 &&
        Number(client.current_balance || 0) > Number(client.credit_limit || 0),
    );
    const pricedClients = clients.filter((client) => Number(client.default_price_per_liter || 0) > 0);
    const avgDefaultPrice = pricedClients.reduce(
      (sum, client) => sum + Number(client.default_price_per_liter || 0),
      0,
    ) / (pricedClients.length || 1);
    return {
      activeCount: activeClients.length,
      totalBalance,
      totalCreditLimit,
      riskyCount: riskyClients.length,
      avgDefaultPrice,
    };
  }, [clients]);

  const openNewModal = () => {
    setFormData(createDefaultClientForm());
    setShowNewModal(true);
  };

  const buildClientPayload = () => ({
    name: formData.name,
    type: 'THIRD_PARTY',
    phone: formData.phone,
    contact_name: formData.station_name || undefined,
    address: formData.quarter || undefined,
    city: formData.city || undefined,
    credit_limit: formData.credit_limit ? Number(formData.credit_limit) : undefined,
    default_price_per_liter: formData.default_price_per_liter ? Number(formData.default_price_per_liter) : undefined,
    segment: formData.category,
    notes: formData.notes || undefined,
  });

  const handleCreate = async () => {
    if (!formData.name || !formData.phone) return;

    const payload = buildClientPayload();

    try {
      const created = await clientsAPI.create(payload);
      setClients((prev) => [created, ...prev]);
      setShowNewModal(false);
      setFormData(createDefaultClientForm());
    } catch (err) {
      console.error('Failed to create client:', err);
      toast.error(messageErreur(err, 'Impossible d’enregistrer le client'));
    }
  };

  const openEditModal = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name || '',
      category: client.segment || 'TIER',
      phone: client.phone || '',
      station_name: client.contact_name || '',
      quarter: client.address || '',
      city: client.city || '',
      credit_limit: client.credit_limit || '',
      default_price_per_liter: client.default_price_per_liter || '',
      notes: client.notes || '',
    });
    setShowEditModal(true);
  };

  const openDetailModal = async (client) => {
    setSelectedClient(client);
    setShowDetailModal(true);
    try {
      const enriched = await clientsAPI.getById(client.id);
      setSelectedClient(enriched || client);
    } catch (err) {
      console.error('Failed to load client detail:', err);
    }
  };

  const handleUpdate = async () => {
    if (!selectedClient) return;
    if (!formData.name || !formData.phone) return;

    const payload = buildClientPayload();

    try {
      const updated = await clientsAPI.update(selectedClient.id, payload);
      setClients((prev) =>
        prev.map((client) => (client.id === updated.id ? updated : client)),
      );
      setShowEditModal(false);
      setSelectedClient(null);
    } catch (err) {
      console.error('Failed to update client:', err);
      toast.error(messageErreur(err, 'Impossible de modifier le client'));
    }
  };

  const confirmDelete = (client) => {
    setSelectedClient(client);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedClient) return;
    try {
      await clientsAPI.delete(selectedClient.id);
      setClients((prev) => prev.filter((c) => c.id !== selectedClient.id));
    } catch (err) {
      console.error('Failed to delete client:', err);
      toast.error(messageErreur(err, 'Impossible de supprimer le client'));
    } finally {
      setShowDeleteModal(false);
      setSelectedClient(null);
    }
  };

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Pilotage clients"
        title="Clients"
        // Sans description: « Suivez encours, crédit, prix de référence et
        // prochaines actions commerciales » nommait les quatre colonnes de la
        // liste qui suit. Les lire une fois en prose puis une fois en tableau
        // ne fait pas deux informations.
        stats={[
          {
            label: 'Clients actifs',
            valeur: pilotage.activeCount,
            unite: `/ ${clients.length}`,
          },
          { label: 'Encours', valeur: formatCompactCurrency(pilotage.totalBalance) },
          {
            label: 'Au-delà du plafond',
            valeur: pilotage.riskyCount,
            ton: pilotage.riskyCount > 0 ? 'critique' : 'neutre',
            aide: 'Clients dont l’encours dépasse le plafond accordé',
          },
        ]}
        statsPlus={
          <dl className="space-y-2 text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Plafonds accordés</dt>
              <dd className="font-semibold tabular-nums">{formatCompactCurrency(pilotage.totalCreditLimit)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Prix moyen par litre</dt>
              <dd className="font-semibold tabular-nums">{formatCurrency(pilotage.avgDefaultPrice)} / L</dd>
            </div>
          </dl>
        }
        actions={
          <>
            {/* « Aide » reste en `ghost` : c'est un recours, pas une étape du
                travail. Une seule action peut être primaire sur un écran, et
                c'est celle qui crée la donnée que la page raconte. */}
            <Button variant="ghost" size="sm">
              <HelpCircle className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Aide
            </Button>
            <Button onClick={openNewModal}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Nouveau client
            </Button>
          </>
        }
      />

      {/* Le compte total passe en `subtitle` : `SectionCard` ne réserve, à
          droite de son en-tête, qu'un bouton d'action — y glisser un chiffre
          inerte le ferait passer pour un lien. */}
      <SectionCard
        icon={BookUser}
        title="Portefeuille client"
        subtitle={`${clients.length} client${clients.length > 1 ? 's' : ''} au total`}
      >
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          {/* La recherche prend toute la largeur restante : c'est le geste
              qu'on répète, le segment ne se change qu'une fois de temps en
              temps. */}
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              className="pl-9"
              placeholder="Rechercher un client, une raison sociale, un contact…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={segmentFilter} onValueChange={setSegmentFilter}>
            <SelectTrigger className="sm:w-56">
              <SelectValue placeholder="Tous les segments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tous les segments</SelectItem>
              <SelectItem value="TIER">Tiers</SelectItem>
              <SelectItem value="OCCASIONAL">Occasionnel</SelectItem>
              <SelectItem value="LOYAL">Fidèle</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <LoadingState shape="table" />
        ) : clients.length === 0 ? (
          /* Portefeuille réellement vide : l'illustration a la place de
             s'afficher, et la phrase dit ce que la page contiendra. */
          <EmptyState
            illustration="client"
            title="Aucun client enregistré pour le moment"
            description="Un client se commande, se livre et se facture. C’est ici que vivent son encours, son plafond de crédit et son prix de référence."
            actionLabel="+ Nouveau client"
            onAction={openNewModal}
          />
        ) : filtered.length === 0 ? (
          /* Des clients existent, le filtre les cache : on le dit autrement,
             sinon l'utilisateur croit avoir perdu ses données. */
          <EmptyState
            variant="filtered"
            title="Aucun résultat"
            description="Aucun client ne correspond à la recherche ou au segment choisi."
          />
        ) : (
          <div className="space-y-2">
            {pagination.lignes.map((client) => {
              const plafond = Number(client.credit_limit || 0);
              const encours = Number(client.current_balance || 0);
              const creditDepasse = plafond > 0 && encours > plafond;

              return (
                <article
                  key={client.id}
                  className="rounded-xl border border-border/70 p-4 transition-colors hover:border-border hover:bg-muted/40"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-foreground">{client.name}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <Badge
                          className={cn(
                            'status-badge',
                            (client.segment || client.type) === 'LOYAL'
                              ? 'bg-muted text-muted-foreground'
                              : 'bg-flame/15 text-flame-ink',
                          )}
                        >
                          {getClientCategoryLabel(client)}
                        </Badge>
                        <Badge
                          className={cn(
                            'status-badge',
                            (client.status || 'ACTIVE') === 'ACTIVE'
                              ? 'bg-success/15 text-success'
                              : 'bg-muted text-muted-foreground',
                          )}
                        >
                          {libelle('clientStatus', client.status || 'ACTIVE')}
                        </Badge>
                        {/* Seul dépassement réel : le rouge doit rester rare
                            pour rester lisible. */}
                        {creditDepasse ? (
                          <Badge className="status-badge bg-destructive/15 text-destructive">
                            Crédit dépassé
                          </Badge>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDetailModal(client)}
                      >
                        {LEXIQUE.ficheClient}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(client)}
                      >
                        Modifier
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => confirmDelete(client)}
                      >
                        Supprimer
                      </Button>
                    </div>
                  </div>

                  {/* Les quatre colonnes du tableau d'origine deviennent une
                      grille : sur un téléphone, un tableau à sept colonnes se
                      lit en défilant de côté, ce que personne ne fait. */}
                  <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 border-t border-border/70 pt-3 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="min-w-0">
                      <p className="text-[0.68rem] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                        Contact
                      </p>
                      <p className="truncate text-sm font-medium text-foreground">
                        {client.phone || 'Téléphone à compléter'}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {client.contact_name || client.city || client.address || 'Contact à compléter'}
                      </p>
                    </div>

                    <div className="min-w-0">
                      <p className="text-[0.68rem] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                        Encours
                      </p>
                      <p className="truncate text-sm font-semibold tabular-nums text-foreground">
                        {formatCurrency(client.current_balance || 0)}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {plafond > 0
                          ? `plafond indicatif ${formatCurrency(client.credit_limit)}`
                          : 'aucun plafond de crédit'}
                      </p>
                    </div>

                    <div className="min-w-0">
                      <p className="text-[0.68rem] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                        Prix / risque
                      </p>
                      <p className="truncate text-sm font-semibold tabular-nums text-foreground">
                        {client.default_price_per_liter
                          ? `${formatCurrency(client.default_price_per_liter)} / L`
                          : 'Prix à définir'}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {LEXIQUE.risque} {client.risk_score ?? LEXIQUE.risqueNonEvalue}
                      </p>
                    </div>

                    <div className="min-w-0">
                      <p className="text-[0.68rem] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                        Prochaine action
                      </p>
                      <p className="truncate text-sm font-medium text-foreground">
                        {creditDepasse
                          ? 'Surveiller l’encours client'
                          : client.default_price_per_liter
                            ? 'Prêt pour commande'
                            : 'Définir prix par litre'}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        Avant allocation ou facture
                      </p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
        {filtered.length > 0 ? <BarrePagination pagination={pagination} nom="clients" /> : null}

        {/* Les trois arguments de vente qui fermaient la carte sur un
            portefeuille vide — « Centralisez vos clients », « Suivez les
            encours », « Personnalisez vos prix » — ont sauté: l'état vide
            juste au-dessus dit déjà à quoi sert la page, et il le dit en
            parlant du travail plutôt que du produit. */}
      </SectionCard>

      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle className="px-6 pt-6">Nouveau client</DialogTitle>
          </DialogHeader>
          <div className="max-h-[calc(90vh-132px)] space-y-4 overflow-y-auto px-6 py-3">
            {/* Sans les trois bandeaux « Identité / Contact / Conditions
                commerciales »: ils annonçaient des sections qui n'existent
                plus, et parlaient d'« équipes dispatch, finance et
                recouvrement » à un exploitant qui est les trois à lui seul. */}
            <div className="space-y-1">
              <Label>Nom</Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Téléphone</Label>
              <Input
                value={formData.phone}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Ville</Label>
              <Input
                value={formData.city}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    city: e.target.value,
                  }))
                }
              />
            </div>
            {/* Le prix par litre reste au premier plan, seul de son bloc: il
                est la valeur de référence du client, celle que la fiche et les
                autres écrans viennent lire. Le plafond de crédit, lui, ne
                bloque rien et peut attendre le dépliant. */}
            <div className="space-y-1">
              <div className="flex items-center gap-1">
                <Label>Prix par litre par défaut</Label>
                <Aide titre="Prix par litre par défaut">Sans lui, la fiche du client reste en « prix à définir ».</Aide>
              </div>
              <Input
                value={formData.default_price_per_liter}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    default_price_per_liter: e.target.value,
                  }))
                }
              />
            </div>

            {/* La catégorie a quitté la création: un client qu'on enregistre
                au téléphone est « Tiers » jusqu'à preuve du contraire, et le
                classer se fait plus tard, quand on le connaît. Elle reste
                modifiable sur la fiche. */}
            <ChampsAvances
              consequence="Sans plafond, aucun dépassement d’encours n’est signalé."
              valeurs={[
                formData.station_name,
                formData.quarter,
                formData.credit_limit,
                formData.notes,
              ]}
            >
              <div className="space-y-1">
                <Label>Contact principal / station</Label>
                <Input
                  value={formData.station_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      station_name: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Adresse / quartier</Label>
                <Input
                  value={formData.quarter}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      quarter: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Plafond de crédit indicatif (facultatif)</Label>
                <Input
                  value={formData.credit_limit}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      credit_limit: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                />
              </div>
            </ChampsAvances>
          </div>
          <DialogFooter className="border-t bg-card px-6 py-4">
            <Button variant="outline" onClick={() => setShowNewModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle className="px-6 pt-6">Modifier le client</DialogTitle>
          </DialogHeader>
          <div className="max-h-[calc(90vh-132px)] space-y-4 overflow-y-auto px-6 py-3">
            <div className="space-y-1">
              <Label>Nom</Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Téléphone</Label>
              <Input
                value={formData.phone}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Ville</Label>
              <Input
                value={formData.city}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    city: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1">
                <Label>Prix par litre par défaut</Label>
                <Aide titre="Prix par litre par défaut">Sans lui, la fiche du client reste en « prix à définir ».</Aide>
              </div>
              <Input
                value={formData.default_price_per_liter}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    default_price_per_liter: e.target.value,
                  }))
                }
              />
            </div>
            {/* La catégorie, elle, ne se règle que depuis la fiche: à la
                création on ne sait pas encore si un client sera fidèle. */}
            <div className="space-y-1">
              <Label>Catégorie</Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, category: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Catégorie client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TIER">Tiers</SelectItem>
                  <SelectItem value="OCCASIONAL">Occasionnel</SelectItem>
                  <SelectItem value="LOYAL">Fidèle</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <MessagesClientSwitch
              client={selectedClient}
              onChange={(mis) => {
                setSelectedClient((prev) => (prev ? { ...prev, notify_trip_steps: mis?.notify_trip_steps } : prev));
                setClients((prev) =>
                  prev.map((item) => (item.id === mis?.id ? { ...item, notify_trip_steps: mis.notify_trip_steps } : item)),
                );
              }}
            />

            <ChampsAvances
              consequence="Sans plafond, aucun dépassement d’encours n’est signalé."
              valeurs={[
                formData.station_name,
                formData.quarter,
                formData.credit_limit,
                formData.notes,
              ]}
            >
              <div className="space-y-1">
                <Label>Contact principal / station</Label>
                <Input
                  value={formData.station_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      station_name: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Adresse / quartier</Label>
                <Input
                  value={formData.quarter}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      quarter: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Plafond de crédit indicatif (facultatif)</Label>
                <Input
                  value={formData.credit_limit}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      credit_limit: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                />
              </div>
            </ChampsAvances>
          </div>
          <DialogFooter className="border-t bg-card px-6 py-4">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdate}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer le client</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Confirmer la suppression du client{' '}
            <span className="font-semibold">{selectedClient?.name}</span> ?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Annuler
            </Button>
            <Button
              className="bg-destructive hover:bg-destructive"
              onClick={handleDelete}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle className="px-6 pt-6">{LEXIQUE.ficheClient}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[calc(90vh-132px)] space-y-4 overflow-y-auto px-6 py-3">
            {/* Sans l'encadré orange qui portait ces deux lignes: teinté, il
                se lisait comme une alerte alors qu'il annonce le plus souvent
                que le compte va bien. Le nom et la phrase sont restés, posés
                en tête de la fiche comme ils le seraient sur un papier. */}
            <div>
              <p className="text-base font-semibold text-foreground">
                {selectedClient?.name}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {Number(selectedClient?.credit_limit || 0) > 0 &&
                Number(selectedClient?.current_balance || 0) > Number(selectedClient?.credit_limit || 0)
                  ? 'Vérifier l’encours avec le client avant la prochaine livraison.'
                  : selectedClient?.default_price_per_liter
                    ? 'Compte prêt pour commande.'
                    : 'Renseigner le prix par litre par défaut.'}
              </p>
            </div>
            {selectedClient && <FinancialStatement partyType="CUSTOMER" partyId={selectedClient.id} />}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Catégorie</p>
                <p className="text-foreground">
                  {getClientCategoryLabel(selectedClient)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Contact principal</p>
                <p className="text-foreground">
                  {selectedClient?.contact_name || 'Non renseigné'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Encours</p>
                <p className="text-foreground">
                  {formatCurrency(selectedClient?.current_balance || 0)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Plafond indicatif</p>
                <p className="text-foreground">
                  {Number(selectedClient?.credit_limit || 0) > 0
                    ? formatCurrency(selectedClient.credit_limit)
                    : 'Aucun plafond'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Prix par litre</p>
                <p className="text-foreground">
                  {selectedClient?.default_price_per_liter ? `${formatCurrency(selectedClient.default_price_per_liter)} / L` : 'À définir'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Ville / quartier</p>
                <p className="text-foreground">
                  {[selectedClient?.city, selectedClient?.address].filter(Boolean).join(' · ') || 'Non renseignée'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Commandes récentes</p>
                {(selectedClient?.recent_orders || []).length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">Aucune commande récente.</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {selectedClient.recent_orders.slice(0, 4).map((order) => (
                      <div key={order.id} className="rounded border bg-card p-2 text-sm">
                        <p className="font-medium">{referenceLisible(order.reference_number)}</p>
                        <p className="text-xs text-muted-foreground">{libelle('statut_commande', order.status)} · {formatCurrency(order.total_amount || 0)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Factures impayées</p>
                {(selectedClient?.unpaid_invoices || []).length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">Aucune facture impayée.</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {selectedClient.unpaid_invoices.slice(0, 4).map((invoice) => (
                      <div key={invoice.id} className="rounded border bg-card p-2 text-sm">
                        <p className="font-medium">{referenceLisible(invoice.invoice_number, 'Sans numéro')}</p>
                        <p className="text-xs text-muted-foreground">{libelle('invoiceStatus', invoice.status)} · {formatCurrency(invoice.amount_remaining || invoice.balance_due || 0)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Notes</p>
              <p className="text-sm text-muted-foreground">
                {selectedClient?.notes ? texteSur(selectedClient.notes) : 'Aucune note'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Contacts</p>
              {(selectedClient?.contacts || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun contact</p>
              ) : (
                <div className="space-y-2">
                  {selectedClient.contacts.map((contact) => (
                    <div key={contact.id} className="text-sm text-muted-foreground">
                      <span className="font-semibold">{contact.name}</span>
                      {contact.role ? ` · ${texteSur(contact.role, '')}` : ''}
                      {contact.phone ? ` · ${contact.phone}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Documents</p>
              {(selectedClient?.documents || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun document</p>
              ) : (
                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                  {selectedClient.documents.map((doc) => (
                    <li key={doc.id}>{doc.name}</li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Relances</p>
              {(selectedClient?.reminders || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune relance</p>
              ) : (
                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                  {selectedClient.reminders.map((reminder) => (
                    <li key={reminder.id}>
                      {texteSur(reminder.message, 'Relance')} · {formatDate(reminder.due_date)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter className="border-t bg-card px-6 py-4">
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
